import { AttendanceStatusEnum } from "@sinch/core/entities/serverEnums";
import { WageCompositionComponent } from "@sinch/types/sinch.types";
import { uuid } from "@sinch/utils/crypto";
import { useCallback } from "react";
import { match } from "ts-pattern";
import {
  ChildrenGetter,
  ChildrenGetterResult,
} from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/rowBuilders";
import {
  JobTableStructureFragment,
  OrderTableStructureFragment,
  PositionEntityCompositionFragment,
  PositionTableStructureFragment,
  ShiftTableStructureFragment,
} from "@sinch/components/application/RatesCompositionTable/queries.types";

export const useChildrenGetter = (): ChildrenGetter =>
  useCallback((data, entityName) => {
    const orderGetter = (): ChildrenGetterResult => ({
      childrenList: (data as OrderTableStructureFragment).jobs ?? [],
      childrenEntityName: "Job" as const,
    });
    const jobGetter = (): ChildrenGetterResult => ({
      childrenList: (data as JobTableStructureFragment).shifts,
      childrenEntityName: "Shift" as const,
    });
    const shiftGetter = (): ChildrenGetterResult => ({
      childrenList: (data as ShiftTableStructureFragment).positions,
      childrenEntityName: "Position" as const,
    });
    const positionGetter = (): ChildrenGetterResult => {
      const position = data as PositionTableStructureFragment & {
        wageComposition: Omit<PositionEntityCompositionFragment, "__typename">;
        priceComposition: Omit<PositionEntityCompositionFragment, "__typename">;
      };
      // Pending attendances are not real seats — the backend's PositionStatisticsManager treats them
      // as unfilled and emits a positionEstimate for each. Mirror that here so the rows shown line up
      // with the totals and don't double-count.
      const confirmedAttendances = position.attendances.filter(
        (attendance) => attendance.status !== AttendanceStatusEnum.ConfirmationPending,
      );
      const emptyPositions = position.size - confirmedAttendances.length;
      const items = Array.from(
        { length: emptyPositions },
        (
          _,
          index
        ): {
          id: string;
          positionId: string;
          wageComposition?: {
            /** Breakdown of wage by component */
            components: Array<Omit<WageCompositionComponent, "ratelistType">>;
            /** Total wage amount */
            total: number;
          };
          priceComposition?: {
            /** Breakdown of wage by component */
            components: Array<Omit<WageCompositionComponent, "ratelistType">>;
            /** Total wage amount */
            total: number;
          };
        } => ({
          id: "__virtual_attendance__" + uuid(),
          positionId: position.id,
          wageComposition: position.wageComposition?.positionEstimates?.[index] ?? { total: 0, components: [] },
          priceComposition: position.priceComposition?.positionEstimates?.[index] ?? { total: 0, components: [] },
        })
      );
      return {
        childrenList: [
          ...confirmedAttendances,
          ...(items as { id: string; [k: string]: unknown }[]),
        ] as ChildrenGetterResult["childrenList"],
        childrenEntityName: "Attendance" as const,
        //childrenBuildRowResolver: buildAttendanceRow as BuildEntityRowFn,
      };
    };
    const attendanceGetter = (): ChildrenGetterResult => ({
      childrenList: [],
      childrenEntityName: "Attendance" as const,
    });

    return match(entityName)
      .with("Order", orderGetter)
      .with("Job", jobGetter)
      .with("Shift", shiftGetter)
      .with("Position", positionGetter)
      .with("Attendance", attendanceGetter)
      .exhaustive();
  }, []);
